import { action, computed, makeObservable, observable } from 'mobx';
import { DispatcherV2Response, GroupData, GroupMode, SortingRequest } from '@bringg/types';
import { ColorGenerator } from '@bringg/react-components';
import { ScrollEvent, notifyNeedToScroll, scrollEmitter } from '@bringg-frontend/monitoring-shared';

import { orderExplorerRootStore } from './order-explorer-root-store';
import { TaskFiltersObject } from '../../shared/components/monitoring-filter-bar/types';
import { tableDefinitionSwitcher } from 'bringg-web/features/monitoring/shared/data/table-definition-switcher';
import { monitoringRootStore } from 'bringg-web/features/monitoring/shared/stores/monitoring-store';

class OrderExplorerStore {
	readonly disposeSignal: AbortSignal;
	refreshAC: AbortController;
	loading = false;
	initialLoading = false;
	scrollTop = 0;
	_groupsColors: Map<string, string> = new Map();
	getRandomColor: () => string;

	constructor(disposeSignal: AbortSignal) {
		makeObservable(this, {
			loading: observable,
			initialLoading: observable,
			scrollTop: observable,
			_groupsColors: observable,
			isScrolled: computed,
			currentTableDefinition: computed,
			setResponse: action,
			setScrollTop: action,
			toggleCollapseAll: action,
			setGroupsResponse: action,
			toggleGroupCollapse: action,
			tableDefinitionChange: action,
			setLoading: action,
			setInitialLoading: action
		});

		scrollEmitter.on(this.setScrollTop);

		this.disposeSignal = disposeSignal;
		this.refreshAC = new AbortController();
		this.disposeSignal.addEventListener(
			'abort',
			() => {
				this.refreshAC.abort();
				scrollEmitter.off(this.setScrollTop);
			},
			{ once: true }
		);
		this.getRandomColor = ColorGenerator.getRandomColor();
	}

	public tableDefinitionChange(changeObj: {
		filters?: TaskFiltersObject;
		sorting?: SortingRequest;
		groupMode?: GroupMode;
	}) {
		this.reset();
		this.resetScroll();

		tableDefinitionSwitcher.switch(
			{
				...changeObj,
				filters: changeObj.filters ? { task: changeObj.filters } : undefined,
				owner: 'order-explorer',
				entityBaseType: 'task'
			},
			GroupMode.TeamRoute
		);
	}

	public get currentTableDefinition() {
		return monitoringRootStore.getStore().tableDefinitionStore.getLatestTableDefinition('order-explorer');
	}

	public toggleGroupCollapse(groupId: string, isCollapsed: boolean) {
		return this.currentTableDefinition.toggleGroupCollapse(groupId, isCollapsed);
	}

	public toggleCollapseAll(isCollapsed: boolean) {
		return this.currentTableDefinition.toggleCollapseAll(isCollapsed);
	}

	public isLoaded(flatArrayIndex: number): boolean {
		return this.currentTableDefinition.isLoaded(flatArrayIndex);
	}

	public setResponse(response: DispatcherV2Response, isLastGroupFull: boolean) {
		return this.currentTableDefinition.setResponse(response, isLastGroupFull);
	}

	public setGroupsResponse(response: DispatcherV2Response, loadedGroupId: string) {
		return this.currentTableDefinition.setGroupsResponse(response, loadedGroupId);
	}

	public isGroupFullyLoaded(group: GroupData) {
		return this.currentTableDefinition.isGroupFullyLoaded(group);
	}

	public setLoading(isLoading: boolean) {
		this.loading = isLoading;
	}

	public setInitialLoading(isLoading: boolean) {
		this.initialLoading = isLoading;
	}

	public isGroupPartiallyLoaded(group: GroupData) {
		return this.currentTableDefinition.isGroupPartiallyLoaded(group);
	}

	resetScroll() {
		notifyNeedToScroll({
			scrollTop: 0,
			target: undefined,
			immediate: true
		});
	}

	setScrollTop = ({ scrollTop }: ScrollEvent) => {
		this.scrollTop = scrollTop;
	};

	private reset = () => {
		const { selectedTasksStore, ordersMapStore } = orderExplorerRootStore.getStore();

		selectedTasksStore.clearSelectedTasks();
		ordersMapStore.reset();
		this._groupsColors.clear();
	};

	private get groupsColor(): Map<string, string> {
		this.currentTableDefinition.groups.forEach(group => {
			const groupId = group.id;

			if (!this._groupsColors.has(groupId)) {
				this._groupsColors.set(groupId, this.getRandomColor());
			}
			if (group.groups) {
				group.groups.forEach(innerGroup => {
					const innerGroupId = innerGroup.id;

					if (!this._groupsColors.has(innerGroupId)) {
						this._groupsColors.set(innerGroupId, this.getRandomColor());
					}
				});
			}
		});
		return this._groupsColors;
	}

	getItemColor = (id: string | number): string => {
		const groupId = this.currentTableDefinition.itemToGroupId.get(Number(id));
		if ((groupId && groupId.includes('null')) || `${id}`.includes('null') || id == null) {
			return '#f02733';
		}
		if (this.groupsColor.has(`${id}`)) {
			return this.groupsColor.get(`${id}`);
		} else {
			return this.getItemColor(groupId);
		}
	};

	get selectedActionColor() {
		const {
			selectedTasksStore: { selectedTasks }
		} = orderExplorerRootStore.getStore();

		if (!selectedTasks.length) {
			return undefined;
		}

		const firstGroupSelected = this.currentTableDefinition.itemToGroupId.get(selectedTasks[0].id);
		const isThereAnotherGroup = selectedTasks.some(
			task => this.currentTableDefinition.itemToGroupId.get(task.id) !== firstGroupSelected
		);

		if (!isThereAnotherGroup) {
			return this.getItemColor(selectedTasks[0].id);
		}

		return undefined;
	}

	get isScrolled() {
		return this.scrollTop > 0;
	}
}

export default OrderExplorerStore;
